
import React, { useState, useEffect } from 'react';
import { 
  Globe, Smartphone, Cloud, TestTube, BarChart3, 
  Code, Layout, ShoppingCart, Database, Server,
  Cpu, Shield, Monitor, Zap, Brain, Eye,
  GitBranch, Lock, TrendingUp, Settings,
  FileText, ChevronRight, Star, ArrowRight
} from 'lucide-react';

const ServicesComponent = () => {
  const [activeCategory, setActiveCategory] = useState(0);
  const [hoveredService, setHoveredService] = useState(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const serviceCategories = [
    {
      id: 1,
      title: "Web Development",
      shortTitle: "Web Dev",
      icon: <Globe className="w-5 h-5" />,
      color: "#4f46e5",
      lightColor: "#818cf8",
      gradient: "from-indigo-500 to-blue-600",
      bgGradient: "from-indigo-50 to-blue-50",
      services: [
        {
          title: "Custom Website Design & Development",
          description: "Responsive, mobile-first layouts with UX/UI design tailored to your brand",
          icon: <Layout className="w-6 h-6" />,
          features: ["Responsive Design", "Mobile-First", "Brand Integration", "CMS Integration"],
          color: "#4f46e5",
          bgColor: "#eef2ff"
        },
        {
          title: "Single-Page Applications (SPA)",
          description: "React.js, Vue.js or Angular frameworks with client-side routing & state management",
          icon: <Code className="w-6 h-6" />,
          features: ["React/Vue/Angular", "State Management", "PWA Support", "Offline Functionality"],
          color: "#7c3aed",
          bgColor: "#f3e8ff"
        },
        {
          title: "Enterprise Web Portals",
          description: "Role-based access control with multi-tenant architectures and ERP/CRM integration",
          icon: <Server className="w-6 h-6" />,
          features: ["Role-based Access", "Multi-tenant", "ERP Integration", "High Availability"],
          color: "#2563eb",
          bgColor: "#dbeafe"
        },
        {
          title: "eCommerce Solutions",
          description: "Shopify, WooCommerce, Magento setups with custom shopping cart & payment integration",
          icon: <ShoppingCart className="w-6 h-6" />,
          features: ["Multiple Platforms", "Payment Gateway", "Inventory Management", "Analytics"],
          color: "#dc2626",
          bgColor: "#fef2f2"
        },
        {
          title: "API Design & Development",
          description: "RESTful and GraphQL API design with OAuth2/JWT authentication and documentation",
          icon: <Database className="w-6 h-6" />,
          features: ["REST & GraphQL", "Authentication", "Rate Limiting", "Documentation"],
          color: "#059669",
          bgColor: "#ecfdf5"
        }
      ]
    },
    {
      id: 2,
      title: "Mobile Development",
      shortTitle: "Mobile",
      icon: <Smartphone className="w-5 h-5" />,
      color: "#10b981",
      lightColor: "#34d399",
      gradient: "from-emerald-500 to-teal-600",
      bgGradient: "from-emerald-50 to-teal-50",
      services: [
        {
          title: "iOS App Development",
          description: "Swift and Objective-C expertise with native UI components & Apple services integration",
          icon: <Smartphone className="w-6 h-6" />,
          features: ["Swift/Objective-C", "Native UI", "Apple Services", "App Store Ready"],
          color: "#374151",
          bgColor: "#f9fafb"
        },
        {
          title: "Android App Development",
          description: "Kotlin and Java expertise with Material Design and Google Play Store publishing",
          icon: <Monitor className="w-6 h-6" />,
          features: ["Kotlin/Java", "Material Design", "Background Processing", "Play Store"],
          color: "#16a34a",
          bgColor: "#f0fdf4"
        },
        {
          title: "Cross-Platform Apps",
          description: "React Native, Flutter, or Xamarin with shared codebase and native-like performance",
          icon: <Cpu className="w-6 h-6" />,
          features: ["React Native/Flutter", "Shared Codebase", "Native Performance", "OTA Updates"],
          color: "#0891b2",
          bgColor: "#ecfeff"
        },
        {
          title: "Hybrid Mobile Apps",
          description: "Ionic or Cordova frameworks for rapid prototyping and cost-effective MVP development",
          icon: <Zap className="w-6 h-6" />,
          features: ["Ionic/Cordova", "Rapid Prototyping", "Native Features", "Cost-effective"],
          color: "#ea580c",
          bgColor: "#fff7ed"
        },
        {
          title: "Mobile App Maintenance & Support",
          description: "Bug fixes, OS compatibility updates, performance optimization and crash analytics",
          icon: <Settings className="w-6 h-6" />,
          features: ["Bug Fixes", "OS Updates", "Performance", "Analytics"],
          color: "#be123c",
          bgColor: "#fdf2f8"
        }
      ]
    },
    {
      id: 3,
      title: "DevOps & Cloud",
      shortTitle: "DevOps",
      icon: <Cloud className="w-5 h-5" />,
      color: "#f59e0b",
      lightColor: "#fbbf24",
      gradient: "from-amber-500 to-orange-600",
      bgGradient: "from-amber-50 to-orange-50",
      services: [
        {
          title: "CI/CD Pipeline Implementation",
          description: "Jenkins, GitLab CI, GitHub Actions with automated build, test, and deployment workflows",
          icon: <GitBranch className="w-6 h-6" />,
          features: ["Jenkins/GitLab CI", "Automated Workflows", "Docker Containers", "IaC"],
          color: "#ea580c",
          bgColor: "#fff7ed"
        },
        {
          title: "Cloud Architecture & Migration",
          description: "AWS, Azure, GCP solution design with lift-and-shift or refactor migrations",
          icon: <Cloud className="w-6 h-6" />,
          features: ["AWS/Azure/GCP", "Migration Strategy", "Cost Optimization", "Auto-scaling"],
          color: "#0369a1",
          bgColor: "#eff6ff"
        },
        {
          title: "Containerization & Orchestration",
          description: "Docker containerization with Kubernetes cluster setup, management and Helm charts",
          icon: <Server className="w-6 h-6" />,
          features: ["Docker", "Kubernetes", "Helm Charts", "Best Practices"],
          color: "#7c3aed",
          bgColor: "#f3e8ff"
        },
        {
          title: "Monitoring & Logging",
          description: "Prometheus, Grafana dashboards with ELK/EFK logging stacks and alerting integration",
          icon: <BarChart3 className="w-6 h-6" />,
          features: ["Prometheus/Grafana", "ELK Stack", "Alerting", "Dashboards"],
          color: "#dc2626",
          bgColor: "#fef2f2"
        },
        {
          title: "Security & Compliance",
          description: "Vulnerability scanning, secrets management and GDPR, HIPAA, SOC-2 advisory",
          icon: <Shield className="w-6 h-6" />,
          features: ["Vulnerability Scanning", "Secrets Management", "Compliance", "Security Advisory"],
          color: "#059669",
          bgColor: "#ecfdf5"
        }
      ]
    },
    {
      id: 4,
      title: "Quality Assurance",
      shortTitle: "QA Testing",
      icon: <TestTube className="w-5 h-5" />,
      color: "#ef4444",
      lightColor: "#f87171",
      gradient: "from-red-500 to-pink-600",
      bgGradient: "from-red-50 to-pink-50",
      services: [
        {
          title: "Manual Testing Services",
          description: "Functional, regression, smoke testing with usability & exploratory testing expertise",
          icon: <Eye className="w-6 h-6" />,
          features: ["Functional Testing", "Regression Testing", "Usability Testing", "Test Design"],
          color: "#dc2626",
          bgColor: "#fef2f2"
        },
        {
          title: "Automated Testing Frameworks",
          description: "Selenium WebDriver, Cypress, Playwright with API and mobile testing capabilities",
          icon: <Cpu className="w-6 h-6" />,
          features: ["Selenium/Cypress", "API Testing", "Mobile Testing", "Automated Frameworks"],
          color: "#7c3aed",
          bgColor: "#f3e8ff"
        },
        {
          title: "Performance & Load Testing",
          description: "JMeter, Gatling load scripts with stress testing and bottleneck analysis",
          icon: <TrendingUp className="w-6 h-6" />,
          features: ["JMeter/Gatling", "Load Testing", "Stress Testing", "Performance Analysis"],
          color: "#ea580c",
          bgColor: "#fff7ed"
        },
        {
          title: "Security Testing (Penetration Testing)",
          description: "OWASP Top 10 assessment with network & application layer penetration tests",
          icon: <Shield className="w-6 h-6" />,
          features: ["OWASP Assessment", "Penetration Testing", "Security Analysis", "Remediation"],
          color: "#059669",
          bgColor: "#ecfdf5"
        },
        {
          title: "Test Management & Reporting",
          description: "TestRail, Zephyr setup with detailed QA dashboards and defect tracking workflows",
          icon: <FileText className="w-6 h-6" />,
          features: ["Test Management", "QA Dashboards", "Defect Tracking", "Reporting"],
          color: "#0891b2",
          bgColor: "#ecfeff"
        }
      ]
    },
    {
      id: 5,
      title: "Data & Analytics",
      shortTitle: "Data Science",
      icon: <BarChart3 className="w-5 h-5" />,
      color: "#8b5cf6",
      lightColor: "#a78bfa",
      gradient: "from-violet-500 to-purple-600",
      bgGradient: "from-violet-50 to-purple-50",
      services: [
        {
          title: "Data Strategy & Consulting",
          description: "Data maturity assessment with roadmap for data governance & technology stack recommendation",
          icon: <TrendingUp className="w-6 h-6" />,
          features: ["Maturity Assessment", "Data Governance", "Technology Stack", "Strategic Roadmap"],
          color: "#7c3aed",
          bgColor: "#f3e8ff"
        },
        {
          title: "Data Engineering & ETL",
          description: "Batch & real-time pipelines with data lake & warehouse design and API-driven ingestion",
          icon: <Database className="w-6 h-6" />,
          features: ["Airflow/Kafka", "Data Warehouse", "Real-time Pipelines", "API Integration"],
          color: "#0369a1",
          bgColor: "#eff6ff"
        },
        {
          title: "Machine Learning & AI Solutions",
          description: "Predictive modeling, NLP solutions, and computer vision pipelines with latest frameworks",
          icon: <Brain className="w-6 h-6" />,
          features: ["ML Models", "NLP Solutions", "Computer Vision", "TensorFlow/PyTorch"],
          color: "#dc2626",
          bgColor: "#fef2f2"
        },
        {
          title: "Business Intelligence & Reporting",
          description: "Dashboards with Power BI, Looker, Tableau and KPI definition with self-service analytics",
          icon: <BarChart3 className="w-6 h-6" />,
          features: ["Power BI/Tableau", "KPI Tracking", "Self-service Analytics", "Custom Dashboards"],
          color: "#ea580c",
          bgColor: "#fff7ed"
        },
        {
          title: "MLOps & Model Deployment",
          description: "Model versioning & monitoring with containerized serving and data drift alerts",
          icon: <Settings className="w-6 h-6" />,
          features: ["MLflow/Seldon", "Model Monitoring", "Container Deployment", "Performance Alerts"],
          color: "#059669",
          bgColor: "#ecfdf5"
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50 to-pink-50 py-8 sm:py-16 px-4">
      {/* Floating Background Elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-32 h-32 bg-blue-200 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse"></div>
        <div className="absolute top-40 right-20 w-24 h-24 bg-purple-200 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse animation-delay-1000"></div>
        <div className="absolute bottom-40 left-20 w-28 h-28 bg-pink-200 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse animation-delay-2000"></div>
        <div className="absolute bottom-20 right-10 w-20 h-20 bg-yellow-200 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse animation-delay-3000"></div>
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header Section */}
        <div
          className={`text-center mb-4 sm:mb-8 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
        >
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 mb-4 sm:mb-6"
           style={{
            background: 'linear-gradient(90deg, #4F46E5 0%, #8B5CF6 50%, #EC4899 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}
          >
            Our{' '}
            <span
              className="bg-clip-text text-transparent"
              style={{
                background: 'linear-gradient(90deg, #4F46E5 0%, #8B5CF6 50%, #EC4899 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
            >
              Services
            </span>
          </h1>
          <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed px-4 text-center">
            Comprehensive software development solutions tailored to drive innovation and accelerate your digital transformation journey.
          </p>
        </div>

        {/* Enhanced Category Navigation */}
        <div className="flex justify-center mb-8 sm:mb-16 px-2 sm:px-4">
          {/* Desktop View */}
          <div className="hidden lg:flex items-center space-x-2 bg-white/80 backdrop-blur-lg rounded-2xl p-2 shadow-2xl border border-white/50">
            {serviceCategories.map((category, index) => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(index)}
                className={`group relative flex items-center px-6 py-4 rounded-xl font-semibold transition-all duration-500 transform hover:scale-105 min-w-0 ${
                  activeCategory === index
                    ? 'text-white shadow-2xl scale-105'
                    : 'text-gray-700 hover:text-gray-900'
                }`}
                style={{
                  background: activeCategory === index 
                    ? `linear-gradient(135deg, ${category.color}, ${category.lightColor})` 
                    : `linear-gradient(135deg, ${category.color}10, ${category.lightColor}08)`
                }}
              >
                <div className="relative mr-3 flex-shrink-0">
                  <div className={`transition-all duration-500 ${activeCategory === index ? 'scale-110 rotate-6' : 'scale-100'}`}>
                    {category.icon}
                  </div>
                </div>
                <span className="relative z-10 text-sm font-medium whitespace-nowrap">{category.title}</span>
              </button>
            ))}
          </div>

          {/* Tablet View */}
          <div className="hidden md:flex lg:hidden items-center space-x-2 bg-white/80 backdrop-blur-lg rounded-2xl p-2 shadow-2xl border border-white/50">
            {serviceCategories.map((category, index) => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(index)}
                className={`group relative flex items-center px-4 py-3 rounded-xl font-semibold transition-all duration-500 transform hover:scale-105 min-w-0 ${
                  activeCategory === index
                    ? 'text-white shadow-2xl scale-105'
                    : 'text-gray-700 hover:text-gray-900'
                }`}
                style={{
                  background: activeCategory === index 
                    ? `linear-gradient(135deg, ${category.color}, ${category.lightColor})` 
                    : `linear-gradient(135deg, ${category.color}15, ${category.lightColor}10)`
                }}
              >
                <div className="relative mr-2 flex-shrink-0">
                  <div className={`transition-all duration-500 ${activeCategory === index ? 'scale-110 rotate-6' : 'scale-100'}`}>
                    {category.icon}
                  </div>
                </div>
                <span className="relative z-10 text-xs font-medium whitespace-nowrap">{category.shortTitle}</span>
              </button>
            ))}
          </div>

          {/* Mobile View */}
          <div className="flex md:hidden w-full overflow-x-auto scrollbar-hide">
            <div className="flex items-center space-x-2 bg-white/80 backdrop-blur-lg rounded-2xl p-2 shadow-2xl border border-white/50 min-w-max mx-2">
              {serviceCategories.map((category, index) => (
                <button
                  key={category.id}
                  onClick={() => setActiveCategory(index)}
                  className={`group relative flex flex-col items-center px-4 py-3 rounded-xl font-semibold transition-all duration-500 transform hover:scale-105 min-w-0 ${
                    activeCategory === index
                      ? 'text-white shadow-2xl scale-105'
                      : 'text-gray-700 hover:text-gray-900'
                  }`}
                  style={{
                    background: activeCategory === index 
                      ? `linear-gradient(135deg, ${category.color}, ${category.lightColor})` 
                      : `linear-gradient(135deg, ${category.color}15, ${category.lightColor}10)`,
                    minWidth: '70px'
                  }}
                >
                  <div className="relative mb-1 flex-shrink-0">
                    <div className={`transition-all duration-500 ${activeCategory === index ? 'scale-110 rotate-6' : 'scale-100'}`}>
                      {category.icon}
                    </div>
                  </div>
                  <span className="relative z-10 text-xs font-medium text-center leading-tight">{category.shortTitle}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Active Category Content */}
        <div className="mb-12 sm:mb-16">
          {serviceCategories.map((category, categoryIndex) => (
            <div
              key={category.id}
              className={`transition-all duration-700 ${
                activeCategory === categoryIndex
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-10 absolute pointer-events-none'
              }`}
            >
              {activeCategory === categoryIndex && (
                <>
                  {/* Category Header */}
                  <div className="text-center mb-8 sm:mb-12">
                    <div 
                      className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 rounded-2xl mb-4 sm:mb-6 shadow-xl"
                      style={{ 
                        background: `linear-gradient(135deg, ${category.color}20, ${category.lightColor}15)`,
                        color: category.color,
                        border: `2px solid ${category.color}30`
                      }}
                    >
                      <div className="scale-125 sm:scale-150">{category.icon}</div>
                    </div>
                    <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">{category.title}</h2>
                    <div 
                      className="w-20 sm:w-24 h-1 rounded-full mx-auto shadow-lg"
                      style={{ background: `linear-gradient(90deg, ${category.color}, ${category.lightColor})` }}
                    ></div>
                  </div>

                  {/* Services Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
                    {category.services.map((service, serviceIndex) => (
                      <div
                        key={serviceIndex}
                        className={`group relative rounded-2xl p-6 sm:p-8 shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border border-white/60 overflow-hidden ${
                          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                        }`}
                        style={{ 
                          background: `linear-gradient(135deg, ${service.bgColor} 0%, white 100%)`,
                          transitionDelay: `${serviceIndex * 100}ms`,
                          borderLeft: `4px solid ${service.color}`
                        }}
                        onMouseEnter={() => setHoveredService(`${categoryIndex}-${serviceIndex}`)}
                        onMouseLeave={() => setHoveredService(null)}
                      >
                        {/* Animated Background Pattern */}
                        <div 
                          className="absolute inset-0 opacity-5 transition-opacity duration-500 group-hover:opacity-10"
                          style={{
                            backgroundImage: `radial-gradient(circle at 20% 20%, ${service.color}30 0%, transparent 50%), radial-gradient(circle at 80% 80%, ${service.color}20 0%, transparent 50%)`
                          }}
                        ></div>

                        {/* Content */}
                        <div className="relative z-10">
                          {/* Service Icon */}
                          <div 
                            className="inline-flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 rounded-xl mb-4 sm:mb-6 transition-all duration-500 group-hover:scale-110 group-hover:rotate-3 shadow-lg"
                            style={{ 
                              background: `linear-gradient(135deg, ${service.color}20, ${service.color}10)`,
                              color: service.color,
                              border: `2px solid ${service.color}20`
                            }}
                          >
                            {service.icon}
                          </div>

                          {/* Service Title */}
                          <h3 
                            className="text-lg sm:text-xl font-bold mb-3 sm:mb-4 transition-all duration-300"
                            style={{ color: service.color }}
                          >
                            {service.title}
                          </h3>

                          {/* Service Description */}
                          <p className="text-gray-600 text-sm mb-4 sm:mb-6 leading-relaxed">
                            {service.description}
                          </p>

                          {/* Features */}
                          <div className="space-y-2 mb-4 sm:mb-6">
                            {service.features.map((feature, featureIndex) => (
                              <div 
                                key={featureIndex}
                                className={`flex items-center text-xs text-gray-600 transition-all duration-500 ${
                                  hoveredService === `${categoryIndex}-${serviceIndex}` ? 'text-gray-800 translate-x-2' : ''
                                }`}
                                style={{ transitionDelay: `${featureIndex * 50}ms` }}
                              >
                                <div 
                                  className="w-2 h-2 rounded-full mr-3 transition-all duration-300 flex-shrink-0 shadow-sm"
                                  style={{ 
                                    background: `linear-gradient(135deg, ${service.color}, ${service.color}80)`
                                  }}
                                ></div>
                                {feature}
                              </div>
                            ))}
                          </div>

                          {/* Learn More Button */}
                          <button 
                            className="flex items-center justify-between w-full py-3 px-4 rounded-lg text-white text-sm font-medium transition-all duration-300 hover:shadow-xl hover:scale-105 group/btn"
                            style={{ 
                              background: `linear-gradient(135deg, ${service.color}, ${service.color}dd)`
                            }}
                          >
                            <span>Learn More</span>
                            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover/btn:translate-x-1" />
                          </button>
                        </div>

                        {/* Floating Decorative Elements */}
                        <div className="absolute top-4 right-4 w-8 h-8 rounded-full opacity-10 animate-pulse" 
                             style={{ backgroundColor: service.color }}></div>
                        <div className="absolute bottom-4 left-4 w-6 h-6 rounded-full opacity-10 animate-pulse" 
                             style={{ backgroundColor: service.color, animationDelay: '1s' }}></div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>

        {/* Call to Action */}
        <div
          className={`text-center rounded-3xl p-8 sm:p-12 transition-all duration-1000 delay-1000 relative overflow-hidden ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
          style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
            color: '#fff'
          }}
        >
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 left-0 w-32 h-32 bg-white rounded-full -translate-x-16 -translate-y-16"></div>
            <div className="absolute top-0 right-0 w-24 h-24 bg-white rounded-full translate-x-12 -translate-y-12"></div>
            <div className="absolute bottom-0 left-0 w-28 h-28 bg-white rounded-full -translate-x-14 translate-y-14"></div>
            <div className="absolute bottom-0 right-0 w-20 h-20 bg-white rounded-full translate-x-10 translate-y-10"></div>
          </div>

          <div className="relative z-10">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4 sm:mb-6 text-white">
              Ready to Start Your Project?
            </h2>
            <p className="text-lg sm:text-xl mb-6 sm:mb-8 max-w-2xl mx-auto text-white/90">
              Let's collaborate to bring your vision to life with cutting-edge technology and expert development.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                className="px-6 sm:px-8 py-3 sm:py-4 font-semibold rounded-full hover:shadow-2xl hover:scale-105 transition-all duration-300 bg-white text-[black]"
              >
                Get Free Consultation
              </button>
              <button
                className="px-6 sm:px-8 py-3 sm:py-4 border-2 border-white font-semibold rounded-full hover:bg-white hover:text-purple-600 transition-all duration-300 text-black"
              >
                View Our Portfolio
              </button>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .animation-delay-1000 {
          animation-delay: 1s;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-3000 {
          animation-delay: 3s;
        }
      `}</style>
    </div>
  );
};

export default ServicesComponent;